export class NotesDrawer {
  private containerEl: HTMLElement | null = null;
  private isVisible: boolean = false;
  private notesContent: string = '# Meeting Notes\n\n## Agenda\n- Discussion on architecture & performance\n- Feature verification and edge cases\n\n## Action Items\n- [x] Unstable network detection & auto-reconnect\n- [x] Closed captions & speech-to-text\n- [x] Collaborative scratchpad & export\n';
  private isPreviewMode: boolean = false;
  private roomId: string = '';

  public onNotesChange?: (content: string) => void;
  public onClose?: () => void;

  constructor(roomId: string) {
    this.roomId = roomId;
  }

  public getIsOpen(): boolean {
    return this.isVisible;
  }

  public setContent(content: string) {
    this.notesContent = content;
    if (this.isVisible && this.containerEl) {
      const textarea = this.containerEl.querySelector('#notes-textarea') as HTMLTextAreaElement;
      if (textarea && document.activeElement !== textarea) {
        textarea.value = content;
      }
      this.updatePreview();
    }
  }

  public toggle(container: HTMLElement) {
    this.isVisible = !this.isVisible;
    if (this.isVisible) {
      this.render(container);
    } else {
      this.hide();
    }
  }

  public hide() {
    this.isVisible = false;
    if (this.containerEl) {
      this.containerEl.innerHTML = '';
    }
    if (this.onClose) this.onClose();
  }

  public render(container: HTMLElement) {
    this.containerEl = container;

    container.innerHTML = `
      <div class="side-drawer" style="width: 360px; display: flex; flex-direction: column; height: 100%; background: var(--bg-surface); border-left: 1px solid var(--border-color); z-index: 50;">
        <!-- Header -->
        <div style="padding: 14px 16px; border-bottom: 1px solid var(--border-subtle); display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.1rem;">📝</span>
            <h3 style="font-size: 1rem; margin: 0; font-weight: 700;">Shared Notes</h3>
          </div>
          <div style="display: flex; align-items: center; gap: 6px;">
            <button id="btn-toggle-notes-preview" class="control-btn" style="width: 28px; height: 28px; font-size: 0.75rem;" title="Toggle Preview / Edit">
              ${this.isPreviewMode ? '✏️' : '👁️'}
            </button>
            <button id="btn-dl-notes-md" class="control-btn" style="width: 28px; height: 28px; font-size: 0.75rem;" title="Download Markdown (.md)">
              ⬇️
            </button>
            <button id="btn-close-notes" class="control-btn" style="width: 28px; height: 28px;">&times;</button>
          </div>
        </div>

        <!-- Body -->
        <div style="flex: 1; padding: 12px; display: flex; flex-direction: column; overflow: hidden;">
          <div id="notes-edit-container" style="flex: 1; display: ${this.isPreviewMode ? 'none' : 'flex'}; flex-direction: column;">
            <textarea id="notes-textarea" style="flex: 1; width: 100%; height: 100%; resize: none; background: rgba(0, 0, 0, 0.25); border: 1px solid var(--border-color); border-radius: var(--radius-md); color: var(--text-primary); font-family: var(--font-mono); font-size: 0.85rem; line-height: 1.5; padding: 10px; outline: none;" placeholder="Collaborative meeting notes... (Markdown supported)">${this.notesContent}</textarea>
          </div>

          <div id="notes-preview-container" style="flex: 1; display: ${this.isPreviewMode ? 'block' : 'none'}; overflow-y: auto; background: rgba(0,0,0,0.15); border: 1px solid var(--border-color); border-radius: var(--radius-md); padding: 12px; font-size: 0.85rem; line-height: 1.6; color: var(--text-primary);">
            <!-- Rendered preview -->
          </div>
        </div>

        <!-- Footer -->
        <div style="padding: 10px 14px; border-top: 1px solid var(--border-subtle); display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: var(--text-muted);">
          <span id="notes-char-count">${this.notesContent.length} chars</span>
          <button id="btn-action-save-notes" class="btn-primary" style="padding: 4px 10px; font-size: 0.75rem; width: auto;">Export .md</button>
        </div>
      </div>
    `;

    this.updatePreview();
    this.bindEvents(container);
  }

  private updatePreview() {
    if (!this.containerEl) return;
    const preview = this.containerEl.querySelector('#notes-preview-container');
    if (!preview) return;

    // Simple markdown line formatter
    const lines = this.notesContent.split('\n');
    let html = '';
    for (const line of lines) {
      if (line.startsWith('# ')) {
        html += `<h2 style="font-size: 1.15rem; margin: 0.5rem 0 0.25rem; font-weight: 700; color: white;">${line.slice(2)}</h2>`;
      } else if (line.startsWith('## ')) {
        html += `<h3 style="font-size: 1rem; margin: 0.5rem 0 0.25rem; font-weight: 600; color: var(--brand-primary);">${line.slice(3)}</h3>`;
      } else if (line.startsWith('- [x] ')) {
        html += `<div style="display: flex; align-items: center; gap: 6px; margin: 4px 0;"><span style="color: #34d399;">☑</span><span>${line.slice(6)}</span></div>`;
      } else if (line.startsWith('- [ ] ')) {
        html += `<div style="display: flex; align-items: center; gap: 6px; margin: 4px 0;"><span style="color: var(--text-muted);">☐</span><span>${line.slice(6)}</span></div>`;
      } else if (line.startsWith('- ')) {
        html += `<li style="margin-left: 1.2rem;">${line.slice(2)}</li>`;
      } else if (line.trim() === '') {
        html += '<br/>';
      } else {
        html += `<p style="margin: 4px 0;">${line}</p>`;
      }
    }
    preview.innerHTML = html;
  }

  private bindEvents(container: HTMLElement) {
    const textarea = container.querySelector('#notes-textarea') as HTMLTextAreaElement;
    const charCount = container.querySelector('#notes-char-count');

    textarea?.addEventListener('input', () => {
      this.notesContent = textarea.value;
      if (charCount) charCount.textContent = `${this.notesContent.length} chars`;
      this.updatePreview();
      if (this.onNotesChange) this.onNotesChange(this.notesContent);
    });

    container.querySelector('#btn-toggle-notes-preview')?.addEventListener('click', () => {
      this.isPreviewMode = !this.isPreviewMode;
      this.render(container);
    });

    const exportNotes = () => {
      const blob = new Blob([this.notesContent], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `verve-meeting-notes-${this.roomId}-${Date.now()}.md`;
      a.click();
      URL.revokeObjectURL(url);
    };

    container.querySelector('#btn-dl-notes-md')?.addEventListener('click', exportNotes);
    container.querySelector('#btn-action-save-notes')?.addEventListener('click', exportNotes);

    container.querySelector('#btn-close-notes')?.addEventListener('click', () => {
      this.hide();
    });
  }
}
